import React, { useId } from "react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import styles from "./EvolutionChart.module.css";
import { formatInteger } from "../utils/formatNumber";
import { formatAxisDate } from "../utils/deriveDateBuckets";

// Map a series colour to a darker companion used as the gradient endpoint.
// Falls back to the same colour so unknown palettes still render cleanly.
const REWIND_GRADIENTS: Record<string, string> = {
    "#86e3ce": "#2bb8c8",
    "#2bb8c8": "#1d8a96",
};
const gradientEndFor = (color: string) => REWIND_GRADIENTS[color.toLowerCase()] || color;

export interface ChartSeriesDef {
    key: string;
    label: string;
    color: string;
}

interface EvolutionChartProps {
    title: string;
    subtitle?: string;
    data: Array<Record<string, unknown> & { date: string }>;
    seriesDefs: ChartSeriesDef[];
    emptyLabel: string;
}

const TooltipBody: React.FC<{
    active?: boolean;
    payload?: any[];
    label?: string;
    seriesDefs: ChartSeriesDef[];
}> = ({ active, payload, label, seriesDefs }) => {
    if (!active || !payload || payload.length === 0) return null;
    const valueByKey = new Map<string, number>();
    for (const item of payload) {
        if (item && item.dataKey) valueByKey.set(item.dataKey, Number(item.value) || 0);
    }
    return (
        <div className={styles.tooltip}>
            <p className={styles.tooltipDate}>{formatAxisDate(String(label || ""))}</p>
            {seriesDefs.map((s) => (
                <div className={styles.tooltipRow} key={s.key}>
                    <span className={styles.tooltipDot} style={{ background: s.color }} />
                    <span>
                        {s.label}: <strong>{formatInteger(valueByKey.get(s.key) ?? 0)}</strong>
                    </span>
                </div>
            ))}
        </div>
    );
};

const EvolutionChart: React.FC<EvolutionChartProps> = ({ title, subtitle, data, seriesDefs, emptyLabel }) => {
    // Stable, unique ids for the SVG gradients — one per (chart instance, series).
    // useId() avoids collisions when two charts coexist on the same page.
    const idBase = useId().replace(/[:]/g, "");
    return (
        <section className={styles.card}>
            <header className={styles.header}>
                <h3 className={styles.title}>{title}</h3>
                {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
            </header>
            {data.length === 0 ? (
                <div className={styles.empty}>{emptyLabel}</div>
            ) : (
                <div className={styles.chartWrap}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data} margin={{ top: 10, right: 16, bottom: 0, left: -4 }}>
                            <defs>
                                {seriesDefs.map((s) => {
                                    const gradientId = `chart-grad-${idBase}-${s.key}`;
                                    return (
                                        <linearGradient key={gradientId} id={gradientId} x1="0" y1="0" x2="1" y2="0">
                                            <stop offset="0%" stopColor={s.color} />
                                            <stop offset="100%" stopColor={gradientEndFor(s.color)} />
                                        </linearGradient>
                                    );
                                })}
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e9eff0" vertical={false} />
                            <XAxis
                                dataKey="date"
                                tickFormatter={formatAxisDate}
                                tick={{ fontSize: 11, fill: "#6b7e84" }}
                                stroke="#e1e8ea"
                                tickLine={false}
                                minTickGap={24}
                            />
                            <YAxis
                                tick={{ fontSize: 11, fill: "#6b7e84" }}
                                stroke="#e1e8ea"
                                tickLine={false}
                                width={40}
                                allowDecimals={false}
                            />
                            <Tooltip content={<TooltipBody seriesDefs={seriesDefs} />} />
                            <Legend
                                verticalAlign="bottom"
                                height={28}
                                iconType="circle"
                                wrapperStyle={{ fontSize: 12, color: "#6b7e84" }}
                            />
                            {seriesDefs.map((s) => (
                                <Line
                                    key={s.key}
                                    type="monotone"
                                    dataKey={s.key}
                                    name={s.label}
                                    stroke={`url(#chart-grad-${idBase}-${s.key})`}
                                    strokeWidth={2.5}
                                    dot={false}
                                    activeDot={{ r: 4, fill: s.color }}
                                    isAnimationActive={false}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </section>
    );
};

export default EvolutionChart;
